/**
 * FloatingWindow - Ventana flotante draggable y resizable
 * Sistema de ventanas estilo escritorio profesional
 * Con múltiples handles de resize y persistencia de estado
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Minus, Square, CornersOut, ArrowsOut, ArrowsClockwise, Printer, FloppyDisk, FilePdf, Export } from '@phosphor-icons/react';
import { useExportHistory } from '@/contexts/ExportHistoryContext';
import { toast } from 'sonner';
import { openSafePrintWindow, sanitizeHtml } from '../utils/safePrint';
import ExportPanel from './ExportPanel';

// Acento por ventana (chrome): oro / plata / cobre / esmeralda
export const WIN_ACCENT_HEX = { gold: '#E8C55C', silver: '#C7CCD6', copper: '#C17544', emerald: '#2FA87F' };
const WIN_ACCENT_ORDER = ['gold', 'silver', 'copper', 'emerald'];
const WIN_ACCENT_NAME = { gold: 'Oro', silver: 'Plata', copper: 'Cobre', emerald: 'Esmeralda' };
// Contador para asignar un acento distinto a cada ventana nueva (rota por la paleta)
let windowAccentCounter = 0;

const FloatingWindow = ({ 
  id,
  title, 
  icon: Icon,
  color = '#00dcff',
  children, 
  onClose,
  onFocus,
  onMinimize,
  onRefresh, // Nuevo: callback para actualizar contenido
  onStateChange, // Callback para guardar estado (posición, tamaño)
  isMinimized = false,
  hideHeader = false, // Nueva prop: ocultar la barra de título
  zIndex = 100,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 600, height: 450 },
  minSize = { width: 350, height: 250 }
}) => {
  // Acento por ventana: cada ventana recuerda su propio acento para distinguirlas
  // de un vistazo. El contenido interno mantiene sus colores (verde/rojo, etc.).
  const [winAccent, setWinAccent] = useState(() => {
    try {
      const stored = localStorage.getItem(`kap_win_accent_${id}`);
      if (stored) return stored;
    } catch { /* noop */ }
    // Ventana nueva: acento distinto automáticamente (rota por la paleta)
    const auto = WIN_ACCENT_ORDER[windowAccentCounter % WIN_ACCENT_ORDER.length];
    windowAccentCounter += 1;
    return auto;
  });
  useEffect(() => {
    try { localStorage.setItem(`kap_win_accent_${id}`, winAccent); } catch { /* noop */ }
  }, [id, winAccent]);
  const cycleAccent = useCallback(() => {
    setWinAccent(prev => WIN_ACCENT_ORDER[(WIN_ACCENT_ORDER.indexOf(prev) + 1) % WIN_ACCENT_ORDER.length]);
  }, []);
  color = WIN_ACCENT_HEX[winAccent] || '#E8C55C';
  const windowRef = useRef(null);
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [preMaximizeState, setPreMaximizeState] = useState(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });
  
  // Detectar si es móvil o tablet
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      // Breakpoint subido a 1024px para incluir tablets
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Hook para historial de exportaciones
  const { addEntry } = useExportHistory();

  // Función para cerrar que primero sale de fullscreen
  const handleClose = useCallback(() => {
    // Si estamos en fullscreen, salir primero
    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        onClose && onClose(id);
      }).catch(() => {
        onClose && onClose(id);
      });
    } else {
      onClose && onClose(id);
    }
  }, [onClose, id]);

  // Estilos de cursor para resize (definido arriba para usar en useEffect)
  const resizeCursors = {
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    ne: 'nesw-resize',
    nw: 'nwse-resize',
    se: 'nwse-resize',
    sw: 'nesw-resize'
  };

  // Notificar cambios de estado al padre
  const notifyStateChange = useCallback(() => {
    if (onStateChange && !isMaximized) {
      onStateChange(id, { position, size });
    }
  }, [id, position, size, isMaximized, onStateChange]);

  // Notificar cuando cambia posición o tamaño (con debounce)
  useEffect(() => {
    const timer = setTimeout(notifyStateChange, 300);
    return () => clearTimeout(timer);
  }, [position, size, notifyStateChange]);

  // Manejar cambios en el estado de fullscreen del navegador
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      
      if (isNowFullscreen) {
        // Entrando en fullscreen: ocultar overflow del body
        document.body.style.overflow = 'hidden';
      } else {
        // Saliendo de fullscreen: restaurar overflow y forzar recálculo del layout
        document.body.style.overflow = '';
        window.dispatchEvent(new Event('resize'));
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Cleanup: salir de fullscreen cuando el componente se desmonta
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      // Si esta ventana estaba en fullscreen, salir al desmontar
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // Toggle fullscreen mode usando la API del navegador
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      // Entrar en fullscreen
      if (windowRef.current) {
        windowRef.current.requestFullscreen().catch(err => {
          console.error('Error al entrar en fullscreen:', err);
          document.documentElement.requestFullscreen();
        });
      } else {
        document.documentElement.requestFullscreen();
      }
    } else {
      // Salir de fullscreen
      document.exitFullscreen();
    }
  }, []);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.window-controls') || e.target.closest('.resize-handle')) return;
    
    onFocus && onFocus(id);
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  }, [position, onFocus, id]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && !isMaximized) {
      const newX = Math.max(-size.width + 100, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    }
    
    if (isResizing && !isMaximized) {
      const deltaX = e.clientX - resizeStart.current.x;
      const deltaY = e.clientY - resizeStart.current.y;
      
      let newWidth = size.width;
      let newHeight = size.height;
      let newX = position.x;
      let newY = position.y;
      
      // Resize desde diferentes direcciones
      if (resizeDirection.includes('e')) {
        newWidth = Math.max(minSize.width, resizeStart.current.width + deltaX);
      }
      if (resizeDirection.includes('w')) {
        const widthDelta = resizeStart.current.width - deltaX;
        if (widthDelta >= minSize.width) {
          newWidth = widthDelta;
          newX = resizeStart.current.posX + deltaX;
        }
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(minSize.height, resizeStart.current.height + deltaY);
      }
      if (resizeDirection.includes('n')) {
        const heightDelta = resizeStart.current.height - deltaY;
        if (heightDelta >= minSize.height) {
          newHeight = heightDelta;
          newY = resizeStart.current.posY + deltaY;
        }
      }
      
      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, isResizing, isMaximized, minSize, resizeDirection, position, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  // Resize handler - soporta múltiples direcciones
  const handleResizeStart = useCallback((direction) => (e) => {
    e.stopPropagation();
    e.preventDefault(); // Evita selección de texto
    onFocus && onFocus(id);
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y
    };
  }, [size, position, onFocus, id]);

  // Toggle maximize
  const handleMaximize = useCallback(() => {
    if (isMaximized) {
      setPosition(preMaximizeState.position);
      setSize(preMaximizeState.size);
      setIsMaximized(false);
    } else {
      setPreMaximizeState({ position, size });
      setPosition({ x: 70, y: 10 });
      setSize({ width: window.innerWidth - 90, height: window.innerHeight - 70 });
      setIsMaximized(true);
    }
  }, [isMaximized, position, size, preMaximizeState]);

  // Toggle minimize
  const handleMinimize = useCallback(() => {
    onMinimize && onMinimize(id);
  }, [onMinimize, id]);

  // Global event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      // Evitar selección de texto mientras se arrastra/redimensiona
      document.body.style.userSelect = 'none';
      document.body.style.cursor = isResizing ? resizeCursors[resizeDirection] || 'default' : 'grabbing';
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, resizeDirection]);

  // Focus on click
  const handleWindowClick = () => {
    onFocus && onFocus(id);
  };

  // Estilos para ventana minimizada (oculta pero mantiene estado)
  const minimizedStyles = isMinimized ? {
    position: 'fixed',
    left: '-9999px',
    top: '-9999px',
    visibility: 'hidden',
    pointerEvents: 'none'
  } : {};

  // MODO MÓVIL: Pantalla completa simplificada
  if (isMobile) {
    return (
      <div
        ref={windowRef}
        className="fixed inset-0 z-[1000] flex flex-col"
        style={{
          background: 'linear-gradient(180deg, rgba(18, 24, 31, 0.99) 0%, rgba(11, 15, 22, 1) 100%)',
          visibility: isMinimized ? 'hidden' : 'visible',
        }}
        data-testid={`floating-window-mobile-${id}`}
      >
        {/* Header móvil - Grande y fácil de tocar */}
        <div
          className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{
            background: `linear-gradient(90deg, ${color}20 0%, transparent 50%, ${color}10 100%)`,
            borderBottom: `2px solid ${color}40`,
          }}
        >
          {/* Título con icono */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {Icon && (
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
                  border: `1px solid ${color}50`,
                }}
              >
                <Icon size={22} weight="fill" style={{ color }} />
              </div>
            )}
            <h3 
              className="font-bold text-base truncate"
              style={{ color }}
            >
              {title}
            </h3>
          </div>

          {/* Controles: Pantalla completa (F11), Minimizar y Cerrar (con etiqueta) */}
          <div className="flex items-center gap-2 shrink-0 ml-2">
            <button
              onClick={handleFullscreen}
              className="h-11 px-2.5 rounded-xl flex items-center gap-1 transition-all active:scale-95"
              style={{
                background: isFullscreen ? `${color}33` : 'rgba(255, 255, 255, 0.06)',
                border: `2px solid ${isFullscreen ? `${color}66` : 'rgba(255, 255, 255, 0.12)'}`,
              }}
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa (F11)'}
              data-testid={`floating-window-mobile-fullscreen-${id}`}
            >
              <ArrowsOut size={20} weight="bold" style={{ color: isFullscreen ? color : '#e4e4e7' }} />
              <span className="text-xs font-bold" style={{ color: isFullscreen ? color : '#e4e4e7' }}>F11</span>
            </button>
            <button
              onClick={handleMinimize}
              className="h-11 px-2.5 rounded-xl flex items-center gap-1 transition-all active:scale-95"
              style={{
                background: 'rgba(234, 179, 8, 0.15)',
                border: '2px solid rgba(234, 179, 8, 0.4)',
              }}
              title="Minimizar"
              data-testid={`floating-window-mobile-minimize-${id}`}
            >
              <Minus size={20} className="text-yellow-400" weight="bold" />
              <span className="text-xs font-bold text-yellow-400">Min</span>
            </button>
            <button
              onClick={handleClose}
              className="h-11 px-2.5 rounded-xl flex items-center gap-1 transition-all active:scale-95"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
              }}
              title="Cerrar"
              data-testid={`floating-window-mobile-close-${id}`}
            >
              <X size={20} className="text-red-400" weight="bold" />
              <span className="text-xs font-bold text-red-400">Cerrar</span>
            </button>
          </div>
        </div>

        {/* Contenido - Pantalla completa */}
        <div 
          className="flex-1 overflow-auto p-4"
          style={{
            overscrollBehavior: 'contain',
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  // MODO ESCRITORIO: Ventana flotante normal
  return (
    <div
      ref={windowRef}
      className="fixed rounded-xl overflow-visible"
      style={{
        left: isMinimized ? '-9999px' : position.x,
        top: isMinimized ? '-9999px' : position.y,
        width: isMaximized ? `calc(100vw - 90px)` : size.width,
        height: isMaximized ? `calc(100vh - 70px)` : size.height,
        zIndex: isMinimized ? -1 : zIndex,
        visibility: isMinimized ? 'hidden' : 'visible',
        pointerEvents: isMinimized ? 'none' : 'auto',
        background: 'linear-gradient(180deg, rgba(18, 24, 31, 0.98) 0%, rgba(11, 15, 22, 0.99) 100%)',
        border: `2px solid ${color}50`,
        boxShadow: `
          0 0 30px ${color}20,
          0 20px 60px rgba(0, 0, 0, 0.5),
          inset 0 1px 0 ${color}30
        `,
        cursor: isDragging ? 'grabbing' : 'default',
        transition: isResizing || isDragging ? 'none' : 'box-shadow 0.2s'
      }}
      onClick={handleWindowClick}
      data-testid={`floating-window-${id}`}
    >
      {/* Efecto de borde superior con gradiente */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl"
        style={{
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`
        }}
      />
      
      {/* Header - Draggable (oculto si hideHeader=true) */}
      {!hideHeader && (
        <div
          className="flex items-center justify-between px-4 py-2.5 select-none rounded-t-xl"
          style={{
            background: `linear-gradient(90deg, ${color}15 0%, transparent 50%, ${color}08 100%)`,
            borderBottom: `1px solid ${color}30`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
        >
        {/* Title con icono */}
        <div className="flex items-center gap-3">
          {Icon && (
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
                border: `1px solid ${color}50`,
                boxShadow: `0 0 15px ${color}30`
              }}
            >
              <Icon size={18} weight="fill" style={{ color, filter: `drop-shadow(0 0 6px ${color})` }} />
            </div>
          )}
          <div>
            <h3 
              className="font-bold text-sm tracking-wide"
              style={{ 
                color: color,
                textShadow: `0 0 10px ${color}60`
              }}
            >
              {title}
            </h3>
          </div>
        </div>

        {/* Window controls */}
        <div className="window-controls flex items-center gap-1.5">
          {/* Selector de acento de la ventana (clic para ciclar) */}
          <button
            onClick={cycleAccent}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            title={`Acento: ${WIN_ACCENT_NAME[winAccent]} · clic para cambiar`}
            data-testid={`window-accent-toggle-${id}`}
          >
            <span
              className="w-3.5 h-3.5 rounded-full border border-white/40"
              style={{ background: color, boxShadow: `0 0 8px ${color}` }}
            />
          </button>

          {/* Tamaño actual */}
          {!isMaximized && (
            <span className="text-[10px] text-gray-500 mr-2 font-mono">
              {Math.round(size.width)}×{Math.round(size.height)}
            </span>
          )}
          
          {/* Refresh button - Nuevo */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110 group"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              title="Actualizar contenido"
            >
              <ArrowsClockwise size={14} className="text-gray-400 group-hover:text-gold-400 transition-colors" />
            </button>
          )}
          
          {/* Fullscreen (F11) */}
          <button
            onClick={handleFullscreen}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110 group"
            style={{
              background: isFullscreen ? `${color}33` : 'rgba(255, 255, 255, 0.05)',
              border: isFullscreen ? `1px solid ${color}66` : '1px solid rgba(255, 255, 255, 0.1)'
            }}
            title={isFullscreen ? "Salir de pantalla completa (ESC)" : "Pantalla completa (F11)"}
          >
            <ArrowsOut size={14} className={`${isFullscreen ? 'text-gold-400' : 'text-gray-400 group-hover:text-gold-400'} transition-colors`} />
          </button>
          
          {/* Minimize */}
          <button
            onClick={handleMinimize}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            title="Minimizar"
          >
            <Minus size={14} className="text-gray-400 hover:text-yellow-400" />
          </button>
          
          {/* Maximize */}
          <button
            onClick={handleMaximize}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            title={isMaximized ? "Restaurar" : "Maximizar"}
          >
            {isMaximized ? (
              <CornersOut size={14} className="text-gray-400 hover:text-green-400" />
            ) : (
              <Square size={14} className="text-gray-400 hover:text-green-400" />
            )}
          </button>
          
          {/* Close */}
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-110 hover:bg-red-500/20"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            title="Cerrar"
          >
            <X size={14} className="text-gray-400 hover:text-red-400" />
          </button>
        </div>
      </div>
      )}

      {/* Content - CON SCROLLBARS SIEMPRE VISIBLES */}
      <div 
        className={`window-content rounded-b-xl ${isFullscreen ? 'fullscreen-content' : ''}`}
        style={{ 
          height: hideHeader ? '100%' : 'calc(100% - 52px)',
          padding: hideHeader ? '0' : (isFullscreen ? '24px' : '16px'),
          overscrollBehavior: 'contain',
          // Forzar scrollbars visibles SIEMPRE con overflow auto
          overflow: 'auto',
          // Scrollbar styling for WebKit browsers
          scrollbarWidth: 'thin',
          scrollbarColor: `${color}99 rgba(30, 30, 40, 0.8)`
        }}
      >
        {/* Wrapper interno para el contenido - permite que el scroll funcione correctamente */}
        <div style={{ 
          height: '100%',
          minHeight: '100%',
          // Asegurar que el contenido pueda expandirse
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Pasar isMaximized e isFullscreen a los children */}
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, { 
                isMaximized, 
                isFullscreen,
                windowSize: isFullscreen 
                  ? { width: window.innerWidth, height: window.innerHeight - 52 }
                  : (isMaximized 
                      ? { width: window.innerWidth - 90, height: window.innerHeight - 70 } 
                      : size)
              });
            }
            return child;
          })}
        </div>
      </div>

      {/* Resize handles - 8 direcciones - ESTILO EXCEL */}
      {!isMaximized && (
        <>
          {/* BORDE DERECHO - Estilo Excel */}
          <div 
            className="resize-handle absolute right-0 top-12 bottom-0 w-2 select-none group hover:bg-gold-500/20"
            style={{ cursor: resizeCursors.e }}
            onMouseDown={handleResizeStart('e')}
          >
            <div 
              className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ 
                background: color,
                boxShadow: `0 0 10px ${color}`
              }}
            />
          </div>
          
          {/* BORDE IZQUIERDO - Estilo Excel */}
          <div 
            className="resize-handle absolute left-0 top-12 bottom-0 w-2 select-none group hover:bg-gold-500/20"
            style={{ cursor: resizeCursors.w }}
            onMouseDown={handleResizeStart('w')}
          >
            <div 
              className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ 
                background: color,
                boxShadow: `0 0 10px ${color}`
              }}
            />
          </div>
          
          {/* BORDE INFERIOR - Estilo Excel */}
          <div 
            className="resize-handle absolute bottom-0 left-0 right-0 h-2 select-none group hover:bg-gold-500/20"
            style={{ cursor: resizeCursors.s }}
            onMouseDown={handleResizeStart('s')}
          >
            <div 
              className="absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ 
                background: color,
                boxShadow: `0 0 10px ${color}`
              }}
            />
          </div>
          
          {/* BORDE SUPERIOR (bajo el título) */}
          <div 
            className="resize-handle absolute top-0 left-4 right-4 h-1 hover:bg-zinc-800/70 hover:border-gold-400 select-none"
            style={{ cursor: resizeCursors.n }}
            onMouseDown={handleResizeStart('n')}
          />
          
          {/* ESQUINAS - Zonas de agarre más grandes */}
          <div 
            className="resize-handle absolute top-0 left-0 w-4 h-4 hover:bg-zinc-800/70 hover:border-gold-400 select-none"
            style={{ cursor: resizeCursors.nw }}
            onMouseDown={handleResizeStart('nw')}
          />
          <div 
            className="resize-handle absolute top-0 right-0 w-4 h-4 hover:bg-zinc-800/70 hover:border-gold-400 select-none"
            style={{ cursor: resizeCursors.ne }}
            onMouseDown={handleResizeStart('ne')}
          />
          <div 
            className="resize-handle absolute bottom-0 left-0 w-4 h-4 hover:bg-zinc-800/70 hover:border-gold-400 select-none"
            style={{ cursor: resizeCursors.sw }}
            onMouseDown={handleResizeStart('sw')}
          />
          
          {/* ESQUINA INFERIOR DERECHA - Indicador visual permanente */}
          <div
            className="resize-handle absolute bottom-0 right-0 w-6 h-6 group select-none"
            style={{ cursor: resizeCursors.se }}
            onMouseDown={handleResizeStart('se')}
          >
            <div className="absolute inset-0 bg-gold-500/0 group-hover:bg-gold-500/20 transition-colors rounded-tl-lg" />
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              className="absolute bottom-0.5 right-0.5 opacity-40 group-hover:opacity-100 transition-opacity"
              style={{ color }}
            >
              <path 
                d="M14 14L14 8M14 14L8 14M11 14L14 11M14 5L5 14" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>
        </>
      )}
      
      {/* Panel de Exportación */}
      <ExportPanel
        isOpen={showExportPanel}
        onClose={() => setShowExportPanel(false)}
        windowId={id}
        windowTitle={title}
        onExport={(exportData) => {
          addEntry({
            type: exportData.type,
            title: title,
            component: id,
            details: `${exportData.type === 'pdf' ? 'PDF' : 'Impresión'} de ${title} (${exportData.sections.length} secciones)`
          });
        }}
      />
    </div>
  );
};

export default FloatingWindow;
